import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, File as FileIcon, Send, RefreshCcw, UploadCloud, Lock, FolderArchive } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import api, { downloadFile, downloadUrl, formatApiError } from "../../lib/api";
import { StatusBadge, CaseTimeline, StatusProgress } from "../../components/StatusTools";
import { StlViewerButton } from "../../components/STLViewer";
import { FilePreviewButton } from "../../components/FilePreview";
import { InvoicePanel } from "../../components/InvoicePanel";

export default function CaseDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [revisionNote, setRevisionNote] = useState("");
  const [revOpen, setRevOpen] = useState(false);
  const [revSending, setRevSending] = useState(false);
  const addFileRef = useRef(null);

  const load = () => api.get(`/cases/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); // eslint-disable-next-line react-hooks/exhaustive-deps -- load is stable per id; re-runs on id change only
  }, [id]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setSending(true);
    try {
      await api.post(`/cases/${id}/messages`, { body: msg.trim() });
      setMsg("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSending(false); }
  };

  const requestRevision = async () => {
    if (!revisionNote.trim()) return;
    setRevSending(true);
    try {
      await api.post(`/cases/${id}/revisions`, { note: revisionNote.trim() });
      toast.success("Revision requested — the planning team has been notified.");
      setRevOpen(false);
      setRevisionNote("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRevSending(false); }
  };

  const addFiles = async (list) => {
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("kind", "intake");
      try {
        await api.post(`/cases/${id}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${f.name} uploaded`);
      } catch (err) { toast.error(`${f.name}: ${formatApiError(err)}`); }
    }
    await load();
  };

  if (data === null) return <div className="flex justify-center py-24"><div className="h-8 w-8 rounded-full border-2 border-sky-600 border-t-transparent animate-spin" /></div>;
  if (data === false) return <p className="py-24 text-center text-slate-500" data-testid="case-not-found">Case not found.</p>;

  const intakeFiles = data.files.filter((f) => f.kind === "intake");
  const planFiles = data.files.filter((f) => f.kind === "plan");
  const planLocked = !!data.invoice && data.invoice.status !== "paid";

  return (
    <div data-testid="case-detail-page">
      <Link to="/portal" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 mb-6" data-testid="case-back-link">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to cases
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display font-black text-3xl tracking-tight font-mono" data-testid="case-id-heading">{data.case_id}</h1>
            <StatusBadge status={data.status} />
          </div>
          <p className="text-sm text-slate-500 mt-1.5">
            {data.patient_ref} · {data.case_type} · {data.arch} arch · submitted {new Date(data.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" data-testid="case-export-btn" className="rounded-full border-slate-300 font-semibold"
            onClick={() => {
              toast.info("Preparing your case archive…");
              downloadUrl(`/cases/${id}/export`, `${data.case_id}-archive.zip`)
                .then(() => toast.success("Case archive downloaded"))
                .catch((e) => toast.error(formatApiError(e)));
            }}>
            <FolderArchive className="h-4 w-4 mr-2" /> Export Archive
          </Button>
          <Dialog open={revOpen} onOpenChange={setRevOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="revision-request-btn" className="rounded-full border-slate-300 font-semibold">
                <RefreshCcw className="h-4 w-4 mr-2" /> Request Revision
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="revision-dialog">
              <DialogHeader><DialogTitle className="font-display font-bold text-xl">Request a revision</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">Describe exactly what should change. The planning team is notified immediately.</p>
              <Textarea rows={5} value={revisionNote} onChange={(e) => setRevisionNote(e.target.value)} data-testid="revision-note-input"
                placeholder="e.g. Reduce upper IPR on 13–12 to 0.2mm, slow posterior staging, add attachments on 24/25…" />
              <Button onClick={requestRevision} disabled={revSending || !revisionNote.trim()} data-testid="revision-submit-btn"
                className="rounded-full bg-sky-600 hover:bg-sky-700 font-bold">
                {revSending ? "Sending…" : "Submit revision request"}
              </Button>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <StatusProgress status={data.status} />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Clinical brief</h2>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div><dt className="text-slate-400 text-xs mb-1">Software preference</dt><dd className="font-semibold">{data.software_preference}</dd></div>
              <div><dt className="text-slate-400 text-xs mb-1">Urgency</dt><dd className="font-semibold capitalize">{data.urgency}</dd></div>
              <div className="sm:col-span-2"><dt className="text-slate-400 text-xs mb-1">Treatment goals</dt><dd className="leading-relaxed whitespace-pre-wrap">{data.treatment_goals}</dd></div>
              {data.clinical_notes && <div className="sm:col-span-2"><dt className="text-slate-400 text-xs mb-1">Clinical notes</dt><dd className="leading-relaxed whitespace-pre-wrap">{data.clinical_notes}</dd></div>}
            </dl>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400">Files</h2>
              <button onClick={() => addFileRef.current?.click()} data-testid="case-add-files-btn"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-600 hover:text-sky-800">
                <UploadCloud className="h-3.5 w-3.5" /> Add files
              </button>
              <input ref={addFileRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            </div>
            {planFiles.length > 0 && (
              <div className="mb-5">
                <p className="text-[11px] font-bold uppercase tracking-widest text-emerald-600 mb-2">Delivered plans</p>
                <ul className="space-y-2">
                  {planFiles.map((f) => <FileRow key={f.id} f={f} accent locked={planLocked} />)}
                </ul>
              </div>
            )}
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2">Intake records</p>
            {intakeFiles.length === 0 ? (
              <p className="text-sm text-slate-400">No intake files uploaded yet.</p>
            ) : (
              <ul className="space-y-2">{intakeFiles.map((f) => <FileRow key={f.id} f={f} />)}</ul>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Messages with the planning team</h2>
            <div className="space-y-4 mb-5 max-h-96 overflow-y-auto pr-1" data-testid="case-messages">
              {data.messages.length === 0 && <p className="text-sm text-slate-400">No messages yet — ask the planning team anything about this case.</p>}
              {data.messages.map((m) => (
                <div key={m.id} className={`flex ${m.sender_role === "client" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${m.sender_role === "client" ? "bg-sky-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                    <p className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${m.sender_role === "client" ? "text-sky-200" : "text-slate-400"}`}>
                      {m.sender_name} · {new Date(m.created_at).toLocaleString()}
                    </p>
                    <p className="leading-relaxed whitespace-pre-wrap">{m.body}</p>
                  </div>
                </div>
              ))}
            </div>
            <form onSubmit={sendMessage} className="flex gap-3" data-testid="message-form">
              <Textarea rows={2} value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="message-input"
                placeholder="Write a message to the planning team…" className="resize-none" />
              <Button type="submit" disabled={sending || !msg.trim()} data-testid="message-send-btn"
                className="self-end rounded-full bg-sky-600 hover:bg-sky-700 h-10 w-10 p-0 shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <InvoicePanel invoice={data.invoice} onPaid={load} />
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-5">Case timeline</h2>
            <CaseTimeline history={data.status_history} />
          </div>
          {data.revisions?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="revisions-panel">
              <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Revisions</h2>
              <ul className="space-y-3">
                {data.revisions.map((r) => (
                  <li key={r.id} className="text-sm border border-slate-100 rounded-lg p-3 bg-slate-50">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${r.status === "open" ? "text-red-500" : "text-emerald-600"}`}>{r.status}</span>
                      <span className="text-[11px] text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-slate-600 leading-relaxed">{r.note}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FileRow({ f, accent, locked }) {
  return (
    <li className={`flex items-center justify-between border rounded-lg px-4 py-2.5 text-sm ${accent ? "border-emerald-200 bg-emerald-50/60" : "border-slate-100 bg-slate-50"}`}>
      <span className="flex items-center gap-2 min-w-0">
        <FileIcon className={`h-4 w-4 shrink-0 ${accent ? "text-emerald-600" : "text-sky-600"}`} />
        <span className="truncate font-medium">{f.filename}</span>
        <span className="text-xs text-slate-400 shrink-0">{(f.size / 1048576).toFixed(1)} MB · {f.uploaded_by}</span>
      </span>
      {locked ? (
        <span className="ml-3 inline-flex items-center gap-1 text-xs font-bold text-amber-600 shrink-0" data-testid={`file-locked-${f.id}`}>
          <Lock className="h-3.5 w-3.5" /> Unlocks after payment
        </span>
      ) : (
        <span className="flex items-center shrink-0">
          <StlViewerButton file={f} />
          <FilePreviewButton file={f} />
          <button onClick={() => downloadFile(f.id, f.filename).catch((e) => toast.error(formatApiError(e)))} data-testid={`file-download-${f.id}`}
            className={`ml-3 inline-flex items-center gap-1 text-xs font-bold ${accent ? "text-emerald-700 hover:text-emerald-900" : "text-sky-600 hover:text-sky-800"}`}>
            <Download className="h-3.5 w-3.5" /> Download
          </button>
        </span>
      )}
    </li>
  );
}
